/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { Anchor, StatusBadge, Text, UnstyledButton } from '@/shared/ui';
import { adsManagerAdUrl, formatBrlFromCents } from '@/shared/utils/ad-dialog-helpers';
import type { TranslateFn } from './publication-row-parts';
import type { PublicationRow, RowBoost } from './types';

/** Text of one boost badge: its daily budget and how long it runs, when known. */
function boostLabel(boost: RowBoost, t: TranslateFn): string {
  if (boost.daily_budget_cents === undefined || boost.duration_days === undefined) {
    return t('publications.boostActiveShort');
  }
  return t('publications.boostActive', {
    budget: formatBrlFromCents(boost.daily_budget_cents),
    days: boost.duration_days,
  });
}

interface LeadAdBadgeProps {
  row: PublicationRow;
  t: TranslateFn;
  onShowAdError?: (row: PublicationRow) => void;
  /** The agency's ad account; lets a running Lead Ad link to Ads Manager. */
  adAccountId?: string | null;
}

/**
 * The Lead Ad state of a row: being created, running, or not created (a
 * click on the latter explains why). Lead Ads and boosts are created after
 * the post, asynchronously, so this is where the user sees their outcome.
 */
function LeadAdBadge({ row, t, onShowAdError, adAccountId }: LeadAdBadgeProps) {
  if (row.lead_ad_status === 'pending') {
    return (
      <Text as="span" data-testid={`lead-ad-pending-${row.key}`}>
        <StatusBadge tone="neutral">{t('publications.leadAdPending')}</StatusBadge>
      </Text>
    );
  }
  if (row.lead_ad_status === 'failed') {
    return (
      <UnstyledButton
        onClick={() => onShowAdError?.(row)}
        className="cursor-pointer hover:opacity-80 transition-opacity"
        data-testid={`lead-ad-failed-${row.key}`}
      >
        <StatusBadge tone="error">
          <Text as="span" className="underline underline-offset-2" data-testid={`lead-ad-failed-label-${row.key}`}>
            {`${t('publications.leadAdFailed')} · ${t('publications.leadAdFailedHint')}`}
          </Text>
        </StatusBadge>
      </UnstyledButton>
    );
  }
  if (row.lead_ad_status === 'created' || row.ad_id) {
    const leadAdId = row.lead_ad_id ?? row.ad_id ?? '';
    if (row.lead_ad_paused) {
      return (
        <Text as="span" data-testid={`lead-ad-paused-${row.key}`}>
          <StatusBadge tone="neutral">{t('publications.leadAdPaused')}</StatusBadge>
        </Text>
      );
    }
    const adsManagerUrl = adsManagerAdUrl(adAccountId, leadAdId);
    if (!adsManagerUrl) {
      return (
        <>
          <Text as="span" data-testid={`lead-ad-badge-${row.key}`}>
            <StatusBadge tone="success">{t('publications.leadAdActive')}</StatusBadge>
          </Text>
        </>
      );
    }
    return (
      <>
      <Anchor
        href={adsManagerUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={t('publications.leadAdOpenInAdsManager')}
        aria-label={`${t('publications.leadAdActive')} - ${t('publications.leadAdOpenInAdsManager')}`}
        className="cursor-pointer hover:opacity-80 transition-opacity"
        data-testid={`lead-ad-badge-${row.key}`}
      >
        <StatusBadge tone="success">
          <Text as="span" className="inline-flex items-center gap-(--space-1)">
            {t('publications.leadAdActive')}
            <ExternalLinkIcon width={11} height={11} aria-hidden="true" data-testid={`lead-ad-badge-icon-${row.key}`} />
          </Text>
        </StatusBadge>
      </Anchor>
      </>
    );
  }
  return null;
}

/**
 * A running boost's badge. Like the Lead Ad badge, it opens the ad in Ads
 * Manager when the agency's ad account is known; without one it is plain text.
 */
function BoostBadge({ boost, t, adAccountId }: { boost: RowBoost; t: TranslateFn; adAccountId?: string | null }) {
  const label = boostLabel(boost, t);
  const adsManagerUrl = adsManagerAdUrl(adAccountId, boost.ad_id);
  if (!adsManagerUrl) return <StatusBadge tone="brand">{label}</StatusBadge>;
  return (
    <Anchor
      href={adsManagerUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={t('publications.boostOpenInAdsManager')}
      aria-label={`${label} - ${t('publications.boostOpenInAdsManager')}`}
      className="cursor-pointer hover:opacity-80 transition-opacity"
      data-testid={`boost-badge-link-${boost.ad_id}`}
    >
      <StatusBadge tone="brand">
        <Text as="span" className="inline-flex items-center gap-(--space-1)">
          {label}
          <ExternalLinkIcon width={11} height={11} aria-hidden="true" />
        </Text>
      </StatusBadge>
    </Anchor>
  );
}

/**
 * The paid-ad badges on a publication row: its Lead Ad state and every boost
 * running on its post.
 */
export function AdStatusBadges({ row, t, onShowAdError, adAccountId }: LeadAdBadgeProps) {
  return (
    <>
      <LeadAdBadge row={row} t={t} onShowAdError={onShowAdError} adAccountId={adAccountId} />
      {row.boosts?.map((boost) => (
        <Text as="span" key={boost.ad_id} className="inline-flex items-center gap-(--space-1)" data-testid={`boost-badge-${boost.ad_id}`}>
          {boost.paused_at ? (
            <StatusBadge tone="neutral">{t('publications.boostPausedShort')}</StatusBadge>
          ) : (
            <BoostBadge boost={boost} t={t} adAccountId={adAccountId} />
          )}
        </Text>
      ))}
    </>
  );
}
